// src/reportWebVitals.js
//
// Real-user Core Web Vitals reporting. Until 2026-04-26 this file was
// dead code — it imported the web-vitals library but no caller wired
// it up to anywhere. Now it sends each metric to GA4 as a custom
// `web_vitals` event (consent-gated via trackEvent). Lets us slice CWV
// by page, country, device class etc. in the same dashboard we already
// use for funnel analytics.
//
// We deliberately do NOT send web-vitals to Sentry. They're not errors,
// and Sentry's Issues feed is for things that need triage. CWV piped
// in as captureMessage created an "issue" per metric per pageview,
// which buried real exceptions under metric noise. (See: 2026-04-26
// — initial Sentry rollout had this and the dashboard was full of
// `web-vitals: LCP=1684` rows within minutes.) For perf trend lines,
// GA4 has the segmentation tools (locale, source, device class) and
// can be wired into Looker Studio if you want change-detection alerts.
//
// API note: we're on web-vitals v2 (getCLS/getFID/etc). When/if we
// upgrade to v3+ we'll switch FID → INP, which is the metric Google
// now uses to rank interactivity. For a marketing site INP and FID
// usually agree — not worth the upgrade churn until we migrate off CRA.

import { trackEvent } from './lib/analytics';

// Each metric arrives as { name, value, delta, id, ... }.
// We forward the rounded value (Google's GA4 dashboards expect integer
// ms, not float ms with random precision). CLS is unitless [0,1] so we
// scale × 1000 to make the integer meaningful.
function report(metric) {
  const value = Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value);

  // GA4 — standard `web_vitals` event so it shows up under Engagement
  // automatically. Including the metric ID lets you join multiple events
  // for the same page-load if you ever export to BigQuery.
  trackEvent('web_vitals', {
    metric_name: metric.name,
    metric_value: value,
    metric_id: metric.id,
    metric_delta: Math.round(metric.delta || 0),
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
  });
}

// Boot — pulls web-vitals lazily so it doesn't add to the initial bundle.
// Each metric calls `report` once when its measurement is final (e.g.
// LCP fires after the largest paint settles, CLS fires at page hide).
if (typeof window !== 'undefined') {
  import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
    getCLS(report);
    getFID(report);
    getFCP(report);
    getLCP(report);
    getTTFB(report);
  }).catch(() => {
    // web-vitals chunk failed to load — non-critical, continue without
    // metrics. Doesn't affect user-visible behaviour.
  });
}

// No exports — this module side-effect-imports the metrics on first
// require. Just `import './reportWebVitals'` from index.js.
